/**
 * useGraph — the React binding for the Entity Graph page.
 *
 * Strict layering: the page calls THIS hook; the hook owns the feature stores
 * (which call the api facade) and the pure {@link buildEntityGraph} model. It
 * HYDRATES full Goal + Workstream documents (so the graph gets the linked-story
 * / initiative id-arrays and contributor/POC people that live only in the S3
 * body) and reads pointer-row summaries for everything else — then folds them
 * into a navigable {@link EntityGraph}. Root selection + traversal depth are UI
 * state; the connected subgraph is derived with {@link connectedSubgraph}.
 *
 * No new backend, no new collection: this reuses the existing generic-store
 * reads the rest of the app already does.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { GoalStore } from '../goals/goalStore';
import { WorkstreamStore } from '../workstreams/workstreamStore';
import { TaskStore } from '../tasks/taskStore';
import { TeamDocumentStore } from '../documents/documentStore';
import { DecisionStore } from '../decisions/decisionStore';
import { ServiceStore } from '../services/serviceStore';
import { ChangeStore } from '../changes/changeStore';
import { DailyUpdateStore } from '../dailyUpdates/dailyUpdateStore';
import { BlockerStore } from '../blockers/blockerStore';
import {
  buildEntityGraph,
  connectedSubgraph,
  graphGoalFromDoc,
  graphRoots,
  graphWorkstreamFromDoc,
  type ConnectedSubgraph,
  type EntityGraph,
  type GraphNode,
  type GraphSource,
} from './graphModel';

/** Default + bounds for the traversal depth control. */
export const GRAPH_MIN_DEPTH = 1;
export const GRAPH_MAX_DEPTH = 3;
export const GRAPH_DEFAULT_DEPTH = 2;

export interface UseGraphResult {
  /** The full assembled graph (all entities + people). */
  graph: EntityGraph;
  /** Goal / workstream / service anchors eligible as a root. */
  roots: GraphNode[];
  /** The currently-selected root key (or ''). */
  rootKey: string;
  setRootKey: (key: string) => void;
  /** Traversal depth (hops from the root). */
  depth: number;
  setDepth: (depth: number) => void;
  /** The connected subgraph around the selected root (undefined when none). */
  subgraph: ConnectedSubgraph | undefined;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const EMPTY_GRAPH: EntityGraph = {
  nodes: new Map(),
  edges: [],
  adjacency: new Map(),
};

export function useGraph(): UseGraphResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      goals: new GoalStore(api),
      workstreams: new WorkstreamStore(api),
      tasks: new TaskStore(api),
      documents: new TeamDocumentStore(api),
      decisions: new DecisionStore(api),
      services: new ServiceStore(api),
      changes: new ChangeStore(api),
      dailyUpdates: new DailyUpdateStore(api),
      blockers: new BlockerStore(api),
    }),
    [api],
  );

  const [source, setSource] = useState<GraphSource | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rootKey, setRootKey] = useState('');
  const [depth, setDepth] = useState(GRAPH_DEFAULT_DEPTH);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load summaries for every collection in parallel...
      const [
        goalSummaries,
        workstreamSummaries,
        tasks,
        documents,
        decisions,
        services,
        changes,
        dailyUpdates,
        blockers,
      ] = await Promise.all([
        stores.goals.listSummaries(),
        stores.workstreams.listSummaries(),
        stores.tasks.listAll(),
        stores.documents.listSummaries(),
        stores.decisions.listSummaries(),
        stores.services.listSummaries(),
        stores.changes.listSummaries(),
        stores.dailyUpdates.listSummaries(),
        stores.blockers.listSummaries(),
      ]);

      // ...then hydrate full Goal + Workstream docs for the id-arrays + people
      // that only live in the S3 body (bounded: goals + workstreams counts).
      const [goalDocs, workstreamDocs] = await Promise.all([
        Promise.all(goalSummaries.map((g) => stores.goals.get(g.id))),
        Promise.all(workstreamSummaries.map((w) => stores.workstreams.get(w.id))),
      ]);

      const next: GraphSource = {
        goals: goalDocs
          .filter((g): g is NonNullable<typeof g> => !!g && !g.deleted)
          .map(graphGoalFromDoc),
        workstreams: workstreamDocs
          .filter((w): w is NonNullable<typeof w> => !!w && !w.deleted)
          .map(graphWorkstreamFromDoc),
        stories: tasks.map((t) => ({
          id: t.id,
          title: t.title,
          status: t.status,
          assignee: t.assignee,
        })),
        documents,
        decisions,
        services,
        changes,
        dailyUpdates,
        blockers,
      };
      setSource(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the entity graph.');
    } finally {
      setLoading(false);
    }
  }, [stores]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const graph = useMemo(
    () => (source ? buildEntityGraph(source) : EMPTY_GRAPH),
    [source],
  );
  const roots = useMemo(() => graphRoots(graph), [graph]);

  // Keep the selected root valid: default to the first root, or clear if gone.
  useEffect(() => {
    if (roots.length === 0) {
      if (rootKey) setRootKey('');
      return;
    }
    if (!rootKey || !roots.some((r) => r.key === rootKey)) {
      setRootKey(roots[0].key);
    }
  }, [roots, rootKey]);

  const subgraph = useMemo(
    () => (rootKey ? connectedSubgraph(graph, rootKey, depth) : undefined),
    [graph, rootKey, depth],
  );

  return {
    graph,
    roots,
    rootKey,
    setRootKey,
    depth,
    setDepth,
    subgraph,
    loading,
    error,
    refresh,
  };
}
